import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import LanguageSelector from '../../components/feature/LanguageSelector';

type Step = 'login' | 'reset' | 'complete-profile';

export default function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('login');
  const [justConfirmed, setJustConfirmed] = useState(false);

  // Login form
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);

  // Reset password
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Profile completion
  const [profileData, setProfileData] = useState({ name: '', company: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Detect email confirmation redirect from Supabase
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const isConfirmation =
      hash.includes('type=signup') ||
      search.includes('type=signup') ||
      (hash.includes('access_token') && !hash.includes('type=recovery'));

    if (isConfirmation) {
      setJustConfirmed(true);
      // Sign out any auto-session Supabase may have created so the client
      // goes through the normal login flow intentionally
      supabase.auth.signOut().catch(() => {});
      // Clean the URL without reloading
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    setShowResendButton(false);
    setResendSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowResendButton(false);
    setResendSuccess(false);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        if (!data.user.email_confirmed_at) {
          setError('Please confirm your email address before logging in. Check your inbox for a confirmation link.');
          setShowResendButton(true);
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }

        const { data: clientData } = await supabase
          .from('clients')
          .select('name, company')
          .eq('user_id', data.user.id)
          .maybeSingle();

        const needsProfile = !clientData || !clientData.name || clientData.name.trim() === '';

        if (needsProfile) {
          setProfileData({ name: '', company: clientData?.company || '' });
          setStep('complete-profile');
          setLoading(false);
          return;
        }

        navigate('/portal');
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');
    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email,
      });
      if (resendError) throw resendError;
      setResendSuccess(true);
      setShowResendButton(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to resend confirmation email');
    } finally {
      setResendLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetSuccess(false);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (resetError) throw resetError;
      setResetSuccess(true);
      setResetEmail('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      setProfileError('Full name is required');
      return;
    }
    setProfileLoading(true);
    setProfileError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Session expired. Please log in again.');

      const { data: existing } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existing) {
        const { error: updateError } = await supabase
          .from('clients')
          .update({
            name: profileData.name.trim(),
            company: profileData.company.trim() || null,
          })
          .eq('user_id', user.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            name: profileData.name.trim(),
            email: user.email ?? '',
            company: profileData.company.trim() || null,
          });
        if (insertError) throw insertError;
      }

      navigate('/portal');
    } catch (err: unknown) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const toggleResetMode = () => {
    setStep(step === 'reset' ? 'login' : 'reset');
    setError('');
    setResetSuccess(false);
    setResetEmail('');
    setShowResendButton(false);
    setResendSuccess(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0d1520] to-[#111827] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">

        {/* Language Selector */}
        <div className="flex justify-end mb-4">
          <LanguageSelector prominent />
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6 cursor-pointer">
            <h1 className="text-3xl font-black text-[#FDB913] tracking-wide">SERENITY LAW</h1>
            <p className="text-white/60 text-sm font-semibold tracking-widest uppercase">Client Portal</p>
          </Link>
        </div>

        {/* ── EMAIL CONFIRMED BANNER ── */}
        {justConfirmed && (
          <div className="mb-5 bg-green-500/15 border border-green-500/40 rounded-xl px-5 py-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-green-500/20 rounded-full flex-shrink-0">
                <i className="ri-checkbox-circle-fill text-green-400 text-xl"></i>
              </div>
              <div>
                <p className="text-green-300 font-black text-sm mb-1">Email Confirmed Successfully!</p>
                <p className="text-white/65 text-xs leading-relaxed">
                  Your account is now verified. Enter your email and password below to sign in and access your client portal.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-2xl">

          {/* ── STEP: COMPLETE PROFILE ── */}
          {step === 'complete-profile' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/40 flex-shrink-0">
                  <i className="ri-user-settings-line text-[#FDB913] text-lg"></i>
                </div>
                <div>
                  <h2 className="text-xl font-black text-white">Complete Your Profile</h2>
                  <p className="text-white/50 text-xs">Just a few details before you enter the portal</p>
                </div>
              </div>

              <div className="bg-[#FDB913]/10 border border-[#FDB913]/30 rounded-lg px-4 py-3 mb-5">
                <div className="flex items-start gap-2">
                  <i className="ri-information-line text-[#FDB913] text-sm flex-shrink-0 mt-0.5"></i>
                  <p className="text-white/70 text-xs leading-relaxed">
                    Your account is verified. Please provide your name so we can personalize your portal experience.
                  </p>
                </div>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Full Name <span className="text-[#FDB913]">*</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.name}
                    onChange={(e) => {
                      setProfileData(prev => ({ ...prev, name: e.target.value }));
                      setProfileError('');
                    }}
                    placeholder="e.g. John Doe"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    Company / Importer Name
                    <span className="text-white/40 text-xs ml-2">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={profileData.company}
                    onChange={(e) => setProfileData(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Your Company Name"
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
                  />
                </div>

                {profileError && (
                  <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    {profileError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={profileLoading}
                  className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(253,185,19,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {profileLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-right-circle-line"></i>
                      Save &amp; Enter Portal
                    </>
                  )}
                </button>
              </form>
            </>
          )}

          {/* ── STEP: LOGIN ── */}
          {step === 'login' && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-black text-white mb-2">
                  {justConfirmed ? 'Sign In to Continue' : 'Client Login'}
                </h2>
                <p className="text-white/60 text-sm">
                  {justConfirmed
                    ? 'Your email is confirmed — sign in below to access your portal.'
                    : 'Access your retainer account and case information'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    required
                    className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
                  />
                  <div className="mt-2 text-right">
                    <button
                      type="button"
                      onClick={toggleResetMode}
                      className="text-[#FDB913] hover:text-white text-sm font-medium transition-colors cursor-pointer"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm">
                    <i className="ri-error-warning-line mr-2"></i>
                    {error}
                  </div>
                )}

                {resendSuccess && (
                  <div className="bg-green-500/20 border border-green-500/50 text-white px-4 py-3 rounded-lg text-sm">
                    <i className="ri-mail-check-line mr-2"></i>
                    Confirmation email resent — check your inbox
                  </div>
                )}

                {showResendButton && (
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resendLoading}
                    className="w-full bg-white/10 hover:bg-white/20 text-[#FDB913] font-bold text-sm py-2.5 rounded-lg transition-all duration-300 border border-[#FDB913]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resendLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-send-line"></i>
                        Resend Confirmation Email
                      </>
                    )}
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(253,185,19,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Signing In...
                    </>
                  ) : (
                    <>
                      <i className="ri-login-box-line"></i>
                      Sign In
                    </>
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="bg-gradient-to-br from-[#FDB913]/15 to-[#FDB913]/5 border-2 border-[#FDB913]/40 rounded-xl px-5 py-4 text-center">
                  <p className="text-white/80 text-sm font-bold mb-3">New to Serenity Law?</p>
                  <Link
                    to="/signup"
                    className="w-full flex items-center justify-center gap-2 bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line text-lg"></i>
                    Create Your Client Account
                  </Link>
                  <p className="text-white/40 text-xs mt-2">Free to sign up · No credit card required</p>
                </div>
              </div>
            </>
          )}

          {/* ── STEP: RESET PASSWORD ── */}
          {step === 'reset' && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-black text-white mb-2">Reset Password</h2>
                <p className="text-white/60 text-sm">Enter your email to receive a password reset link</p>
              </div>

              {resetSuccess ? (
                <div className="space-y-5">
                  <div className="bg-green-500/20 border border-green-500/50 text-white px-4 py-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <i className="ri-mail-check-line text-green-400 text-xl flex-shrink-0 mt-0.5"></i>
                      <div>
                        <p className="font-bold mb-1">Check your email</p>
                        <p className="text-sm text-white/80">
                          We&apos;ve sent a password reset link to your email address. Click the link to create a new password.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={toggleResetMode}
                    className="w-full bg-white/10 hover:bg-white/20 text-white font-bold text-base py-3 rounded-lg transition-all duration-300 border border-white/20 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Login
                  </button>
                </div>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-5">
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">Email Address</label>
                    <input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => { setResetEmail(e.target.value); setError(''); }}
                      placeholder="your@email.com"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50 focus:border-[#FDB913]/50 transition-all text-sm"
                    />
                  </div>

                  {error && (
                    <div className="bg-red-500/20 border border-red-500/50 text-white px-4 py-3 rounded-lg text-sm">
                      <i className="ri-error-warning-line mr-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#FDB913] hover:bg-[#e6a800] text-black font-black text-base py-3 rounded-lg transition-all duration-300 shadow-lg hover:shadow-[0_0_20px_rgba(253,185,19,0.4)] flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-mail-send-line"></i>
                        Send Reset Link
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={toggleResetMode}
                    className="w-full bg-white/10 hover:bg-white/20 text-white font-bold text-base py-3 rounded-lg transition-all duration-300 border border-white/20 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Login
                  </button>
                </form>
              )}
            </>
          )}

          <div className="mt-6 bg-black/30 border border-white/10 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <i className="ri-shield-keyhole-fill text-[#FDB913] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-white/60 text-xs leading-relaxed">
                <strong className="text-white/80">Secure Login.</strong> All communications are encrypted with 256-bit SSL.
              </p>
            </div>
          </div>
        </div>

        {/* International Contact Button */}
        <div className="mt-4 bg-gradient-to-r from-[#FDB913]/10 to-[#FDB913]/5 border border-[#FDB913]/40 rounded-xl px-5 py-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#FDB913]/20 rounded-full border border-[#FDB913]/50 flex-shrink-0">
              <i className="ri-global-line text-[#FDB913] text-base"></i>
            </div>
            <div>
              <p className="text-white text-sm font-black tracking-wide">International Clients</p>
              <p className="text-white/55 text-xs">Need help in your language? Contact us directly.</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <a
              href="tel:+15093960741"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FF1A1A] hover:bg-[#cc0000] text-white font-black text-sm py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-base"></i>
              Call: 1-(509) 396-0741
            </a>
            <a
              href="mailto:hello@mail.tariffrefund.attorney"
              className="flex-1 flex items-center justify-center gap-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 border border-[#FDB913]/50 text-[#FDB913] font-black text-sm py-2.5 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-base"></i>
              Email Us
            </a>
          </div>
          <p className="text-white/40 text-[10px] text-center mt-2 leading-relaxed">
            Translation tools available · Attorney speaks English only
          </p>
        </div>

        <div className="text-center mt-4">
          <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
